import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import * as header from '@ulule/header'
import { useDramaticallyInefficientMediaQuery } from '@owl-nest/hooks'
import {
  ConfiguredNavMenu,
  Wrapper,
  Screen,
  PM_MENU_WIDTH,
  type BreakpointType,
  BREAKPOINT_PROJECT_EDIT_MODE,
} from '@owl-nest/project-manager-nav'
import { message } from '@owl-nest/redux-wrapper/components'
import * as plume from '@ulule/owl-kit-components/next'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'
import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'

import { useDisplayMode, type DisplayMode as DisplayModeType } from '../hooks/useDisplayMode'
import * as projectModel from '../models/project'
import { type RootState } from '../../reducers'
import * as UFE from '../../UFE'

import { PreviewBar } from '../views/detail/components/PreviewBar'

import { LilSam } from '../components/LilSam'

type HeaderLayoutProps = {
  headerRef?: React.Ref<HTMLElement>
  className?: string
  style?: React.CSSProperties
  isHeaderSticky?: boolean
}

export function HeaderLayoutComponent({
  headerRef,
  className,
  style,
  isHeaderSticky,
}: HeaderLayoutProps): React.ReactElement<HeaderLayoutProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const role = models.project.getEditingUserRole(project, UFE.CURRENT_USER.is_staff)
  const userConfig = env.useUserEnv()

  const stickyRefHeader = React.useRef<HTMLDivElement>(null)

  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  const breakpointType: BreakpointType = isEditMode ? 'custom-project-edit-mode' : 'tablet'
  const breakpoint =
    breakpointType === 'custom-project-edit-mode' ? BREAKPOINT_PROJECT_EDIT_MODE : plume.BREAKPOINTS.TABLET

  const isTablet = useDramaticallyInefficientMediaQuery(breakpoint)

  const auth = useAuth()
  const isOwner = auth.type === 'loggedin' && auth.user.id === project.owner.id

  // If owner menu is visible or the custom color is white, set color to blue.
  // Otherwise, use color chosen by user.
  const backgroundColor = project.background_color?.[userConfig.lang]
  const theme = {
    colors: {
      primary: isEditMode
        ? plume.COLORS.PRIMARY_BLUE_500
        : backgroundColor && plume.utils.colors.hexToRgb(backgroundColor, 0) !== 'rgb(255,255,255)'
          ? backgroundColor
          : plume.COLORS.PRIMARY_BLUE_500,
    },
  }

  function setRef(node: HTMLDivElement): void {
    ;(stickyRefHeader as any).current = node
    if (headerRef) {
      if (typeof headerRef === 'function') {
        headerRef(node)
      } else {
        ;(headerRef as any).current = node
      }
    }
  }

  return (
    <HeaderWrapper ref={setRef} $displayMode={displayMode} style={style} className={className}>
      {displayMode === 'preview' && <PreviewBar />}
      <header.Header theme={theme} enableSubnav={false} />

      {isEditMode && !isTablet && (
        <ConfiguredNavMenu
          mobileBreakpoint={isEditMode ? 'custom-project-edit-mode' : undefined}
          project={project}
          role={role}
          screen={Screen.MY_PROJECT}
        />
      )}

      {displayMode !== 'preview' && (
        <BannerContainer $isEditMode={isEditMode} $breakpoint={breakpointType}>
          <message.Banner
            key="error-alert"
            contexts={[
              'bo.success',
              'extratime',
              'extratime_billing',
              'extratime_ended',
              'funded',
              'funded_billing',
              'mobile',
              'pending-changes',
            ]}
          />
        </BannerContainer>
      )}

      <HiddenOnSticky $isHidden={isHeaderSticky}>
        {isOwner && project.status !== api.ProjectStatus.REFUSED && <LilSam />}
      </HiddenOnSticky>
    </HeaderWrapper>
  )
}

export const HeaderLayout = styled(HeaderLayoutComponent)``

const BannerContainer = styled.div<{ $isEditMode: boolean; $breakpoint: BreakpointType }>`
  position: absolute;
  z-index: 0;

  ${({ $breakpoint }) => {
    if ($breakpoint === 'tablet') {
      return css`
        @media screen and ${plume.BREAKPOINTS.TABLET} {
          margin-left: ${({ $isEditMode }) => ($isEditMode ? PM_MENU_WIDTH : '0')};
          width: ${({ $isEditMode }) => ($isEditMode ? `calc(100% - ${PM_MENU_WIDTH})` : '100%')};
        }
      `
    }
    if ($breakpoint === 'custom-project-edit-mode') {
      return css`
        @media screen and ${BREAKPOINT_PROJECT_EDIT_MODE} {
          margin-left: ${({ $isEditMode }) => ($isEditMode ? PM_MENU_WIDTH : '0')};
          width: ${({ $isEditMode }) => ($isEditMode ? `calc(100% - ${PM_MENU_WIDTH})` : '100%')};
        }
      `
    }
  }}
`

const HiddenOnSticky = styled.div<{ $isHidden?: boolean }>`
  opacity: 1;
  position: absolute;
  top: 0px;
  transition: 0.1s;
  width: 100%;
  z-index: ${plume.ZINDEX.NAVIGATION_MENU};
  display: none;

  @media screen and ${BREAKPOINT_PROJECT_EDIT_MODE} {
    display: block;
  }

  ${({ $isHidden }) => {
    if ($isHidden) {
      return css`
        opacity: 0;
        top: -5px;
      `
    } else {
      return css`
        opacity: 1;
      `
    }
  }};
`

const HeaderWrapper = styled.div<{ $displayMode: DisplayModeType }>`
  pointer-events: none;
  z-index: ${plume.ZINDEX.HEADER};

  ${header.HeaderContent}, ${BannerContainer}, ${Wrapper}, ${HiddenOnSticky} {
    pointer-events: all;
  }
`
